import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import { useAtom } from "jotai";
import { Heart, type LucideIcon, Mail, Megaphone } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useAddAgentDialog } from "@/components/AddAgentDialog";
import { useAdvertiseDialog } from "@/components/AdvertiseDialog";
import { Logo } from "@/components/brand/logo";
import { MobileMenuButton } from "@/components/header/MobileMenuButton";
import { signOutAndNavigate } from "@/components/header/shared";
import { UserMenu } from "@/components/header/UserMenu";
import { AnimatedThemeToggler } from "@/components/ui/animated-theme-toggler";
import { useSession } from "@/hooks/auth-hooks";
import {
	CATEGORY_ICONS,
	coerceAgentCategories,
	getAgentCategoryLabel,
} from "@/lib/agent-categories";
import { SOCIAL_LINKS } from "@/lib/constants";
import { type Currency, currencyAtom } from "@/lib/currency";
import { homepageLeaderboardQueryOptions } from "@/lib/leaderboard.query";

const FALLBACK_ICON: LucideIcon = Heart;

// Shared content container so header, main, and footer line up at one width.
// Outer gutter matches PublicPageLayout; the inner column matches page content.
export const PUBLIC_GUTTER = "w-full px-4 sm:px-6";
export const PUBLIC_COLUMN = "mx-auto w-full max-w-5xl";
export const PUBLIC_CONTAINER = `${PUBLIC_GUTTER} ${PUBLIC_COLUMN}`;

export function PublicHeader() {
	const { user, session, isPending } = useSession();
	const location = useLocation();
	const { openAdvertiseDialog } = useAdvertiseDialog();
	const { openAddAgentDialog } = useAddAgentDialog();
	const [isSigningOut, setIsSigningOut] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const isImpersonating = !!session?.impersonatedBy;

	const handleSignOut = async () => {
		if (isSigningOut) return;
		setIsSigningOut(true);
		try {
			await signOutAndNavigate(location.pathname);
		} catch (error) {
			console.error("Sign out failed:", error);
			setIsSigningOut(false);
		}
	};

	const navLinkBase =
		"font-mono text-sm uppercase tracking-[0.14em] px-2.5 py-1.5 transition-colors duration-150 hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background";
	const navLinkClass = (active: boolean) =>
		`${navLinkBase} ${active ? "text-foreground" : "text-muted-foreground"}`;
	const advertiseNavClass =
		"inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.14em] px-2.5 py-1.5 border border-brand/40 text-brand transition-colors duration-150 hover:bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background";

	return (
		<header
			className="fixed top-0 left-0 right-0 z-50 border-b border-border"
			style={{
				background: "color-mix(in srgb, var(--background) 92%, transparent)",
				backdropFilter: "blur(18px)",
				WebkitBackdropFilter: "blur(18px)",
			}}
		>
			<div className={PUBLIC_GUTTER}>
				<div
					className={`${PUBLIC_COLUMN} flex h-14 items-center justify-between`}
				>
					<div className="flex items-center gap-7">
						<Link
							to="/"
							search={{ unsubscribed: undefined }}
							className="flex items-center self-center"
						>
							<Logo size="sm" disableLink />
						</Link>
						{user ? (
							<nav className="hidden items-center gap-1 md:flex">
								<Link
									to="/dashboard"
									className={navLinkClass(
										location.pathname.startsWith("/dashboard"),
									)}
								>
									My agents
								</Link>
								<Link
									to="/agent"
									className={navLinkClass(location.pathname === "/agent")}
								>
									API
								</Link>
							</nav>
						) : (
							<nav className="hidden items-center gap-1 md:flex">
								<Link
									to="/agent"
									className={navLinkClass(location.pathname === "/agent")}
								>
									API
								</Link>
								<button
									type="button"
									onClick={() => openAdvertiseDialog("public-header")}
									className={advertiseNavClass}
								>
									<Megaphone className="size-3.5 shrink-0" />
									Advertise
								</button>
							</nav>
						)}
					</div>
					<div className="flex items-center gap-2 h-10">
						{!isPending &&
							(user ? (
								<>
									<button
										type="button"
										onClick={() => openAdvertiseDialog("public-header")}
										className={`${advertiseNavClass} hidden md:inline-flex`}
									>
										<Megaphone className="size-3.5 shrink-0" />
										Advertise
									</button>
									<UserMenu
										user={user}
										isImpersonating={isImpersonating}
										isSigningOut={isSigningOut}
										onSignOut={() => {
											void handleSignOut();
										}}
									/>
								</>
							) : (
								<>
									<Link
										to="/login"
										className="hidden min-h-10 items-center whitespace-nowrap border border-border px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-[color,border-color,transform] hover:border-brand/40 hover:text-brand active:scale-[0.96] md:inline-flex"
									>
										Sign in
									</Link>
									<button
										type="button"
										onClick={() => openAddAgentDialog()}
										className="hidden min-h-10 min-w-[210px] items-center justify-center whitespace-nowrap bg-foreground px-4 font-mono text-sm uppercase tracking-[0.14em] text-background transition-[background-color,transform] hover:bg-foreground/90 active:scale-[0.96] md:inline-flex"
									>
										+ Add your agent
									</button>
									<MobileMenuButton
										isOpen={mobileMenuOpen}
										onToggle={() => setMobileMenuOpen((open) => !open)}
										visibilityClass="md:hidden"
									/>
								</>
							))}
					</div>
				</div>
			</div>
			{!isPending && !user && (
				<AnimatePresence>
					{mobileMenuOpen && (
						<m.div
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: "auto" }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
							className="md:hidden overflow-hidden border-t border-border bg-background/95 backdrop-blur-sm"
						>
							<nav className="flex flex-col gap-2 p-4">
								<button
									type="button"
									onClick={() => {
										setMobileMenuOpen(false);
										openAdvertiseDialog("public-mobile-menu");
									}}
									className={`${advertiseNavClass} w-full justify-center`}
								>
									<Megaphone className="size-3.5 shrink-0" />
									Advertise
								</button>
								<div className="h-px bg-border my-1" />
								<Link
									to="/login"
									onClick={() => setMobileMenuOpen(false)}
									className="inline-flex min-h-11 items-center justify-center border border-border px-4 font-mono text-sm uppercase tracking-[0.14em] text-foreground transition-[color,border-color] hover:border-brand/40 hover:text-brand"
								>
									Sign in
								</Link>
								<button
									type="button"
									onClick={() => {
										setMobileMenuOpen(false);
										openAddAgentDialog();
									}}
									className="inline-flex min-h-11 items-center justify-center bg-foreground px-4 font-mono text-sm uppercase tracking-[0.14em] text-background transition-[background-color] hover:bg-foreground/90"
								>
									+ Add your agent
								</button>
							</nav>
						</m.div>
					)}
				</AnimatePresence>
			)}
		</header>
	);
}

function useCategoriesWithAgents() {
	const { data } = useQuery(homepageLeaderboardQueryOptions);
	return Array.from(
		new Set(
			(data?.agents ?? []).flatMap((a) => coerceAgentCategories(a.categories)),
		),
	).toSorted();
}

export function BrowseByCategory() {
	const categoriesWithAgents = useCategoriesWithAgents();
	return (
		<div className={PUBLIC_COLUMN}>
			<div className="pt-12 pb-16">
				<div className="flex items-baseline gap-3 pb-5">
					<h2
						className="font-mono text-[clamp(1.125rem,2.2vw,1.5rem)] leading-none tracking-[-0.02em] text-foreground"
						style={{ fontWeight: 400 }}
					>
						<span className="text-brand">/</span>categories
					</h2>
				</div>
				<div className="flex flex-wrap gap-2">
					{categoriesWithAgents.map((cat) => {
						const Icon = CATEGORY_ICONS[cat] ?? FALLBACK_ICON;
						return (
							<Link
								key={cat}
								to="/categories/$category"
								params={{ category: cat }}
								className="inline-flex items-center gap-1.5 border border-border bg-muted/20 px-3 py-1.5 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:border-brand/60 hover:bg-brand/5 hover:text-brand"
							>
								<Icon className="size-3 shrink-0" />
								{getAgentCategoryLabel(cat)}
							</Link>
						);
					})}
				</div>
			</div>
		</div>
	);
}

export function PublicFooter({ children }: { children?: ReactNode }) {
	const [currency, setCurrency] = useAtom(currencyAtom);
	const { openAdvertiseDialog } = useAdvertiseDialog();
	const { user } = useSession();
	const location = useLocation();
	const showCategories = !(
		location.pathname.startsWith("/login") ||
		location.pathname.startsWith("/signup")
	);
	return (
		<>
			{children}
			{showCategories && <BrowseByCategory />}
			<footer className="border-t border-border">
				<div className={`${PUBLIC_COLUMN} @container/footer`}>
					<div className="py-8">
						<div className="flex flex-col gap-10 @3xl/footer:flex-row @3xl/footer:gap-16">
							<div className="flex max-w-sm flex-col gap-6 @3xl/footer:w-72 @3xl/footer:shrink-0">
								<Logo size="lg" />
								<p className="text-pretty font-mono text-sm leading-[1.7] text-muted-foreground">
									The public revenue leaderboard for AI agents. Verified MRR,
									transparent rankings, and the founders shipping them.
								</p>
								<a
									href="https://verifieddr.com/website/agentmrr-com"
									target="_blank"
									rel="noopener noreferrer"
									className="w-fit"
								>
									<img
										src="https://verifieddr.com/badge/agentmrr-com.svg"
										alt="Verified DR - Verified Domain Rating for agentmrr.com"
										width={220}
										height={68}
										className="block h-[68px] w-auto max-w-none dark:hidden"
									/>
									<img
										src="https://verifieddr.com/badge/agentmrr-com-dark.svg"
										alt="Verified DR - Verified Domain Rating for agentmrr.com"
										width={220}
										height={68}
										className="hidden h-[68px] w-auto max-w-none dark:block"
									/>
								</a>
								<a
									href="https://x.com/thijs_smudde"
									target="_blank"
									rel="noopener noreferrer"
									className="group flex w-fit items-center gap-2 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-[#1DA1F2]"
								>
									<img
										src="/thijs.webp"
										alt="Thijs Smudde"
										className="size-6 rounded-full object-cover"
										loading="lazy"
									/>
									Built by Thijs Smudde
								</a>
							</div>
							<div className="grid flex-1 grid-cols-2 gap-x-10 gap-y-8 @2xl/footer:grid-cols-3">
								<FooterColumn label="Navigation">
									<Link
										to="/"
										search={{ unsubscribed: undefined }}
										className={footerLinkClass}
									>
										Home
									</Link>
									<Link to="/categories" className={footerLinkClass}>
										Categories
									</Link>
									<Link to="/founders" className={footerLinkClass}>
										Top Founders
									</Link>
									<Link to="/stats" className={footerLinkClass}>
										Stats
									</Link>
									{user && (
										<Link to="/dashboard" className={footerLinkClass}>
											Dashboard
										</Link>
									)}
									{!user && (
										<>
											<Link to="/login" className={footerLinkClass}>
												Log in
											</Link>
											<Link to="/signup" className={footerLinkClass}>
												Sign up
											</Link>
										</>
									)}
									<button
										type="button"
										onClick={() => openAdvertiseDialog("public-footer")}
										className="flex w-fit items-center gap-1.5 text-left font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-brand"
									>
										<Megaphone className="size-3 shrink-0" />
										Advertise
									</button>
								</FooterColumn>
								<FooterColumn label="Resources">
									<Link to="/docs" className={footerLinkClass}>
										Documentation
									</Link>
									<Link to="/agent" className={footerLinkClass}>
										CLI &amp; API
									</Link>
									<Link to="/docs/submit" className={footerLinkClass}>
										Submit an agent
									</Link>
									<Link to="/blog" className={footerLinkClass}>
										Blog
									</Link>
									<Link to="/terms" className={footerLinkClass}>
										Terms
									</Link>
									<Link to="/privacy" className={footerLinkClass}>
										Privacy
									</Link>
								</FooterColumn>
								<FooterColumn label="From the maker">
									<a
										href="https://agentreacher.com"
										target="_blank"
										rel="noopener noreferrer"
										className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-brand"
									>
										AgentReacher
									</a>
									<a
										href="https://brandreel.com"
										target="_blank"
										rel="noopener noreferrer"
										className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-brand"
									>
										BrandReel
									</a>
									<a
										href="https://verifieddr.com"
										target="_blank"
										rel="noopener noreferrer"
										className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-brand"
									>
										VerifiedDR
									</a>
								</FooterColumn>
							</div>
						</div>
						<div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
							<div className="flex flex-wrap items-center gap-4">
								<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
									© {new Date().getFullYear()} AgentMRR
								</span>
							</div>
							<div className="flex items-center gap-3">
								<div
									className="flex items-center border border-border"
									role="radiogroup"
									aria-label="Currency"
								>
									{(["USD", "EUR"] as Currency[]).map((c) => (
										<button
											key={c}
											type="button"
											role="radio"
											aria-checked={currency === c}
											onClick={() => setCurrency(c)}
											className={`px-2.5 py-1 font-mono text-sm uppercase tracking-[0.14em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset ${
												currency === c
													? "bg-foreground text-background"
													: "text-muted-foreground hover:text-brand"
											}`}
										>
											{c}
										</button>
									))}
								</div>
								<AnimatedThemeToggler size="sm" showLabel />
								<a
									href={SOCIAL_LINKS.email}
									aria-label="Email AgentMRR"
									className="flex size-8 items-center justify-center border border-border text-muted-foreground transition-colors hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
								>
									<Mail className="size-4" />
								</a>
							</div>
						</div>
					</div>
				</div>
			</footer>
		</>
	);
}

function FooterColumn({
	label,
	to,
	children,
}: {
	label: string;
	to?: string;
	children: ReactNode;
}) {
	const headingClass =
		"font-mono text-sm uppercase tracking-[0.14em] text-foreground";
	return (
		<div className="space-y-3">
			{to ? (
				<Link
					to={to}
					className={`${headingClass} block w-fit transition-colors hover:text-brand`}
				>
					&gt; {label}
				</Link>
			) : (
				<p className={headingClass}>&gt; {label}</p>
			)}
			<div className="flex flex-col gap-2">{children}</div>
		</div>
	);
}

const footerLinkClass =
	"font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-brand";
